import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubdomainProvider, useSubdomain } from './contexts/SubdomainContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { PricingProvider } from './contexts/PricingContext';
import { Toaster } from './components/ui/sonner';
import AnalyticsLoader from './components/AnalyticsLoader';
import api from './services/api';
import './App.css';

// Pages

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import UserProfileForm from './pages/UserProfileForm';
import OnboardingStep1 from './pages/OnboardingStep1';
import OnboardingStep2 from './pages/OnboardingStep2';
import CompanySetup from './pages/CompanySetup';
import UserDashboard from './pages/UserDashboard';
import NewRequest from './pages/NewRequest';
import RequestDetails from './pages/RequestDetails';
import RequestsPage from './pages/RequestsPage';
import MyVerifiedLOIsPage from './pages/MyVerifiedLOIsPage';
import DealsPage from './pages/DealsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import SupportPage from './pages/SupportPage';
import VideosPage from './pages/VideosPage';
import ProfileEdit from './pages/ProfileEdit';
import TierVerificationPage from './pages/TierVerificationPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminBrokerQuestions from './pages/AdminBrokerQuestions';
import AdminBuyerQuestions from './pages/AdminBuyerQuestions';
import AdminLOIQuestions from './pages/AdminLOIQuestions';
import AdminUserQuestions from './pages/AdminUserQuestions';
import AdminRequests from './pages/AdminRequests';
import AdminRequestDetail from './pages/AdminRequestDetail';
import AdminUserApprovals from './pages/AdminUserApprovals';
import AdminManageUsers from './pages/AdminManageUsers';
import AdminUserDetails from './pages/AdminUserDetails';
import AdminUserEdit from './pages/AdminUserEdit';
import AdminAccountTypes from './pages/AdminAccountTypes';
import AdminTierApprovals from './pages/AdminTierApprovals';
import AdminLeads from './pages/AdminLeads';
import MyCompanyListing from './pages/MyCompanyListing';
import PublicSellingProcess from './pages/PublicSellingProcess';
import ShareSellingProcess from './pages/ShareSellingProcess';
import AdminTradosAgentChat from './pages/AdminTradosAgentChat';
import AdminTradosAgentOverview from './pages/AdminTradosAgentOverview';
import AdminTradosAgentTasks from './pages/AdminTradosAgentTasks';
import AdminTradosAgentSuggestions from './pages/AdminTradosAgentSuggestions';
import AdminTradosAgentHistory from './pages/AdminTradosAgentHistory';
import AdminTradosAgentMemory from './pages/AdminTradosAgentMemory';
import AdminTradosAgentFiles from './pages/AdminTradosAgentFiles';
import AdminTradosAgentSettings from './pages/AdminTradosAgentSettings';
import AdminTierQuestions from './pages/AdminTierQuestions';
import AdminDeals from './pages/AdminDeals';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AdminSupport from './pages/AdminSupport';
import AdminVideos from './pages/AdminVideos';
import AdminDealCategories from './pages/AdminDealCategories';
import AdminAnnouncementCategories from './pages/AdminAnnouncementCategories';
import AdminSettings from './pages/AdminSettings';
import AdminThemeSettings from './pages/AdminThemeSettings';
import AdminEmailLogs from './pages/AdminEmailLogs';
import AdminLogFiles from './pages/AdminLogFiles';
import AdminProcessSteps from './pages/AdminProcessSteps';
import AdminQuoteRequests from './pages/AdminQuoteRequests';
import AdminQuoteRequestDetail from './pages/AdminQuoteRequestDetail';
import QuoteRequestDetail from './pages/QuoteRequestDetail';
import VerifyEmailPage from './pages/VerifyEmailPage';
import EmailVerificationRequired from './pages/EmailVerificationRequired';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminUploadSettings from './pages/AdminUploadSettings';
import AdminUploadFolder from './pages/AdminUploadFolder';
import AdminSidebarSettings from './pages/AdminSidebarSettings';
import AdminDocuments from './pages/AdminDocuments';
import AdminSecuritySettings from './pages/AdminSecuritySettings';

// Main Admin Pages (Multi-Tenant)
import MainAdminCompanies from './pages/MainAdminCompanies';
import MainAdminApplications from './pages/MainAdminApplications';
import MainAdminMembershipPlans from './pages/MainAdminMembershipPlans';
import AdminMembershipSettings from './pages/AdminMembershipSettings';
import AdminFinancialOverview from './pages/AdminFinancialOverview';
import AdminFinancialSubscribers from './pages/AdminFinancialSubscribers';
import AdminFinancialPayments from './pages/AdminFinancialPayments';
import AdminFinancialForecast from './pages/AdminFinancialForecast';
import AdminFinancialReceipts from './pages/AdminFinancialReceipts';
import AdminCommodities from './pages/AdminCommodities';
import MainAdminStripeSettings from './pages/MainAdminStripeSettings';
import MainAdminAPISettings from './pages/MainAdminAPISettings';
import AdminCoupons from './pages/AdminCoupons';

// Company Registration & Verification
import CompanyRegistration from './pages/CompanyRegistration';
import AdminCompanyApprovalSteps from './pages/AdminCompanyApprovalSteps';
import AdminCompanyApprovalQuestions from './pages/AdminCompanyApprovalQuestions';
import CompanyVerificationPortal from './pages/CompanyVerificationPortal';
import AdminCompanyVerifications from './pages/AdminCompanyVerifications';
import AdminProcessTemplates from './pages/AdminProcessTemplates';
import AdminQuestionTemplates from './pages/AdminQuestionTemplates';
import AdminProcessTemplateEdit from './pages/AdminProcessTemplateEdit';
import AdminThemePresets from './pages/AdminThemePresets';

// Company Admin Pages
import CompanySettings from './pages/CompanySettings';
import Billing from './pages/Billing';
import SubscriptionRequired from './pages/SubscriptionRequired';
import CompanySetupWizard from './pages/CompanySetupWizard';
import CompanyAnalytics from './pages/CompanyAnalytics';
import CompanyQuestions from './pages/CompanyQuestions';
import AdminPublicTheme from './pages/AdminPublicTheme';
import AdminAnalyticsSettings from './pages/AdminAnalyticsSettings';
import InviteUsers from './pages/InviteUsers';
import PlatformAnalyticsDashboard from './pages/PlatformAnalyticsDashboard';
import AdminIntegrationSettings from './pages/AdminIntegrationSettings';
import AuthCallback from './pages/AuthCallback';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import GlobalUserAccessPage from './pages/GlobalUserAccessPage';
import AdminAccessRequests from './pages/AdminAccessRequests';
import AdminGlobalUsers from './pages/AdminGlobalUsers';
import MyReferrals from './pages/MyReferrals';
const ReferralTree = React.lazy(() => import('./pages/ReferralTree'));
import AdminExternalServices from './pages/AdminExternalServices';
// AdminLOIVerificationItems is now a tab inside AdminExternalServices
import PublicLOIVerification from './pages/PublicLOIVerification';
import DirectorySearch from './pages/DirectorySearch';
import DirectoryCompanyDetail from './pages/DirectoryCompanyDetail';
import AdminDirectory from './pages/AdminDirectory';
import AdminDirectoryDump from './pages/AdminDirectoryDump';
import EditBusinessInfo from './pages/EditBusinessInfo';
import NetworkBadge from './pages/NetworkBadge';
import DocsIndex from './pages/DocsIndex';
import DocPage from './pages/DocPage';
import AdminDirectoryClaims from './pages/AdminDirectoryClaims';
import VerifyLOIPage from './pages/VerifyLOIPage';
import LOIQRValidation from './pages/LOIQRValidation';
import AdminEmailTemplates from './pages/AdminEmailTemplates';
import AdminNewsletters from './pages/AdminNewsletters';
import AdminPosting from './pages/AdminPosting';
import AdminCreatedLOIs from './pages/AdminCreatedLOIs';
import AdminIntelSettings from './pages/AdminIntelSettings';
import LOICreationWizard from './pages/LOICreationWizard';
import LOICreatedVerification from './pages/LOICreatedVerification';
import DisclaimerPage from './pages/DisclaimerPage';
import AdminDisclaimerSettings from './pages/AdminDisclaimerSettings';
import AdminDashboardControl from './pages/AdminDashboardControl';
import MyCreatedLOIs from './pages/MyCreatedLOIs';
import MyTodos from './pages/MyTodos';
import ChatWidget from './components/ChatWidget';


// Brand color for loading spinner
const BRAND_BLUE = '#2B4B6A';

// Global chat widget that appears on every page
const GlobalChatWidget = () => {
  const { user } = useAuth();
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';
  if (isEmbed) return null;

  return (
    <ChatWidget
      agentType={user ? 'user' : 'public'}
      isAuthenticated={!!user}
      primaryColor={BRAND_BLUE}
      title={user ? 'Amanda — Trados Assistant' : 'Brandon — Trados Help'}
      placeholder={user ? 'Ask Amanda anything...' : 'Ask Brandon anything...'}
      welcomeMessage={
        user
          ? `Hi${user.first_name ? ` ${user.first_name}` : ''}! I'm Amanda, your Trados assistant. How can I help you?`
          : 'Hello! I\'m Brandon from Trados. I can help you with LOI verification, account setup, or answer any questions. How can I assist you?'
      }
    />
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, requireEmailVerification = true }) => {
  const { user, loading } = useAuth();
  const { company: subdomainCompany, isSubdomain, loading: subdomainLoading, subdomain } = useSubdomain();
  // Normalize the path so route-guard comparisons work whether the app is
  // served at root (legacy) or under /admin (domain-consolidation cutover).
  const rawPath = window.location.pathname;
  const location = rawPath === '/admin'
    ? '/'
    : (rawPath.startsWith('/admin/') ? rawPath.slice('/admin'.length) : rawPath);
  const [accessChecked, setAccessChecked] = React.useState(false);
  const [hasAccess, setHasAccess] = React.useState(null); // null = not checked yet

  // Check company access when on a subdomain
  React.useEffect(() => {
    const checkCompanyAccess = async () => {
      // Not on a subdomain - skip access check
      if (!isSubdomain) {
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }

      // On a subdomain but company not found - still allow access 
      // (the subdomain might be invalid or API failed)
      if (isSubdomain && !subdomainCompany) {
        console.warn('On subdomain but company not found:', subdomain);
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }

      // Not logged in - skip access check (will redirect to login)
      if (!user) {
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }

      // Skip access check for main admins - they have access to everything
      if (user.role === 'admin' || user.role === 'main_admin') {
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }

      // Check if this is the user's home company (compare as strings)
      const userCompanyId = String(user.company_id || '');
      const subdomainCompanyId = String(subdomainCompany.id || '');
      
      if (userCompanyId && subdomainCompanyId && userCompanyId === subdomainCompanyId) {
        setHasAccess(true);
        setAccessChecked(true);
        return;
      }

      // Check if user has approved access to this company
      try {
        const response = await api.get(`/company-access/check/${subdomainCompany.id}`);
        setHasAccess(response.data.has_access === true);
      } catch (error) {
        console.error('Error checking company access:', error);
        // On error, deny access by default (safer)
        setHasAccess(false);
      }
      setAccessChecked(true);
    };

    if (!loading && !subdomainLoading) {
      checkCompanyAccess();
    }
  }, [user, isSubdomain, subdomainCompany, loading, subdomainLoading, subdomain]);

  // Show loading while checking access
  if (loading || subdomainLoading || !accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to access request page if user doesn't have access to this subdomain
  if (isSubdomain && subdomainCompany && hasAccess === false && location !== '/request-access') {
    return <Navigate to="/admin/request-access" replace />;
  }

  // Check if user is any type of admin
  const isAnyAdmin = ['admin', 'main_admin', 'company_admin'].includes(user.role);

  if (adminOnly && !isAnyAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Skip onboarding and email verification checks for admin users
  if (isAnyAdmin) {
    return children;
  }

  // Check if user needs to verify email (only for non-admin users)
  // Allow access to verification-related pages
  const isVerificationPage = location === '/verify-email-required' || location === '/verify-email';
  if (requireEmailVerification && user.email_verified === false && !isVerificationPage) {
    return <Navigate to="/admin/verify-email-required" replace />;
  }

  // Check if user needs to complete onboarding
  const isOnboardingPath = location.startsWith('/onboarding') || location === '/welcome' || location.startsWith('/verify');
  const isAccessRequestPath = location === '/request-access';
  const isPublicUserPath = ['/dashboard', '/deals', '/announcements', '/support', '/videos', '/requests', '/profile', '/verify', '/my-tasks', '/my-referrals', '/my-verified-lois', '/my-created-lois', '/invite', '/create-loi', '/verify-loi-internal'].some(
    p => location === p || location.startsWith(p + '/')
  );
  
  // Allow access to request-access page regardless of onboarding status
  if (isAccessRequestPath) {
    return children;
  }
  
  // Allow access to dashboard and public user pages even with incomplete profile
  if (isPublicUserPath) {
    return children;
  }
  
  // If not on onboarding path and onboarding incomplete, redirect to appropriate step
  if (!isOnboardingPath) {
    // Only redirect if user type not selected
    if (!user.user_type) {
      return <Navigate to="/admin/welcome" replace />;
    }

    // Only redirect if verification not complete AND user type is selected
    if (user.user_type && !user.has_completed_verification) {
      return <Navigate to="/admin/onboarding/step2" replace />;
    }
  }

  return children;
};

// Role-aware chooser for /admin/videos: admins see the manager (add/edit),
// everyone else sees the viewer library.
const VideosRoleGate = () => {
  const { user } = useAuth();
  const isAdmin = user?.role && ['admin', 'main_admin', 'company_admin'].includes(user.role);
  return isAdmin ? <AdminVideos /> : <VideosPage />;
};

// Main Admin Route - Only allows main admins (not company admins)
const MainAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow main_admin or admin role
  const isMainAdmin = ['admin', 'main_admin'].includes(user.role);
  
  if (!isMainAdmin) {
    // Redirect company admins to their dashboard
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
      </div>
    );
  }

  if (user) {
    // Skip onboarding checks for admin users (main_admin, admin, company_admin)
    if (['admin', 'main_admin', 'company_admin'].includes(user.role)) {
      return <Navigate to="/admin" replace />;
    }
    
    // Check if email is verified
    if (user.email_verified === false) {
      return <Navigate to="/admin/verify-email-required" replace />;
    }
    
    // All logged-in users go to dashboard (dashboard shows appropriate warnings)
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <SubdomainProvider>
      <AuthProvider>
        <ThemeProvider>
          <TranslationProvider>
            <PricingProvider>
              <BrowserRouter>
                <AnalyticsLoader />
                <AppRoutes />
              </BrowserRouter>
            </PricingProvider>
          </TranslationProvider>
        </ThemeProvider>
      </AuthProvider>
    </SubdomainProvider>
  );
}

// Separate component to handle routing with OAuth callback detection
function AppRoutes() {
  const location = window.location;
  
  // Check URL fragment for session_id (OAuth callback) BEFORE normal routing
  // This must happen synchronously to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return (
      <div className="App">
        <AuthCallback />
        <Toaster position="top-right" />
      </div>
    );
  }
  
  return (
    <div className="App">
      <Routes>
              {/* Landing page for main domain, login for subdomains */}
              <Route path="/" element={<PublicRoute><Navigate to="/login" replace /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/register/company" element={<CompanyRegistration />} />
            {/* Short invite link — /register/7f3k2q. The long
                ?ref_user=&ref_company= form above still works. */}
            <Route path="/register/:code" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
            <Route path="/verify-loi" element={<PublicLOIVerification />} />
            <Route path="/directory" element={<DirectorySearch />} />
            <Route path="/requests/info" element={<PublicSellingProcess />} />
            {/* Short share links — /selling/wave and /selling/wave/7f3k2q.
                The long ?ref_user=&ref_company= form above still works. */}
            <Route path="/selling/:ref" element={<PublicSellingProcess />} />
            <Route path="/selling/:ref/:code" element={<PublicSellingProcess />} />
            <Route path="/directory/:slug" element={<DirectoryCompanyDetail />} />
            <Route path="/docs" element={<DocsIndex />} />
            <Route path="/docs/:slug" element={<DocPage />} />
            <Route path="/loi-check/:verificationId" element={<LOIQRValidation />} />
            <Route path="/create-loi" element={<LOICreationWizard />} />
            <Route path="/loi-created/:loiId" element={<LOICreatedVerification />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/verify-email-required" element={<ProtectedRoute requireEmailVerification={false}><EmailVerificationRequired /></ProtectedRoute>} />
            
            {/* Global User Access Request Page */}
            <Route path="/request-access" element={<ProtectedRoute requireEmailVerification={false}><GlobalUserAccessPage /></ProtectedRoute>} />
            
            <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
            <Route path="/onboarding/profile" element={<ProtectedRoute><UserProfileForm /></ProtectedRoute>} />
            <Route path="/onboarding/step1" element={<ProtectedRoute><OnboardingStep1 /></ProtectedRoute>} />
            <Route path="/onboarding/step2" element={<ProtectedRoute><OnboardingStep2 /></ProtectedRoute>} />
            <Route path="/company-setup" element={<ProtectedRoute><CompanySetup /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
            <Route path="/requests/new" element={<ProtectedRoute><NewRequest /></ProtectedRoute>} />
            <Route path="/requests/:id" element={<ProtectedRoute><QuoteRequestDetail /></ProtectedRoute>} />
            <Route path="/deals" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
            <Route path="/deals/:id" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
            <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
            <Route path="/announcements/:id" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/support/:id" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/videos" element={<ProtectedRoute><VideosPage /></ProtectedRoute>} />
            <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
            <Route path="/verify/:tier" element={<ProtectedRoute><TierVerificationPage /></ProtectedRoute>} />
            <Route path="/invite" element={<ProtectedRoute><InviteUsers /></ProtectedRoute>} />
            <Route path="/my-referrals" element={<ProtectedRoute><MyReferrals /></ProtectedRoute>} />
            <Route path="/my-verified-lois" element={<ProtectedRoute><MyVerifiedLOIsPage /></ProtectedRoute>} />
            <Route path="/my-created-lois" element={<ProtectedRoute><MyCreatedLOIs /></ProtectedRoute>} />
            <Route path="/my-tasks" element={<ProtectedRoute><MyTodos /></ProtectedRoute>} />
            <Route path="/verify-loi-internal" element={<ProtectedRoute><VerifyLOIPage /></ProtectedRoute>} />

            {/* ============================================================
                /admin/* ALIASES for authenticated app pages.
                Domain-consolidation cutover: the React app will live under
                www.trados.app/admin while WordPress owns the root. These are
                ADDITIVE — the root routes above remain as working aliases so
                nothing breaks before the server-side routing flip. Public
                pages (login, register, verify-loi, create-loi, directory,
                loi-check, loi-created, disclaimer) intentionally stay at root.
                ============================================================ */}
            <Route path="/admin/request-access" element={<ProtectedRoute requireEmailVerification={false}><GlobalUserAccessPage /></ProtectedRoute>} />
            <Route path="/admin/verify-email-required" element={<ProtectedRoute requireEmailVerification={false}><EmailVerificationRequired /></ProtectedRoute>} />
            <Route path="/admin/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
            <Route path="/admin/onboarding/profile" element={<ProtectedRoute><UserProfileForm /></ProtectedRoute>} />
            <Route path="/admin/onboarding/step1" element={<ProtectedRoute><OnboardingStep1 /></ProtectedRoute>} />
            <Route path="/admin/onboarding/step2" element={<ProtectedRoute><OnboardingStep2 /></ProtectedRoute>} />
            <Route path="/admin/company-setup" element={<ProtectedRoute><CompanySetup /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
            <Route path="/admin/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
            <Route path="/admin/requests/new" element={<ProtectedRoute><NewRequest /></ProtectedRoute>} />
            <Route path="/admin/requests/:id" element={<ProtectedRoute><QuoteRequestDetail /></ProtectedRoute>} />
            <Route path="/admin/deals" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
            <Route path="/admin/deals/:id" element={<ProtectedRoute><DealsPage /></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
            <Route path="/admin/announcements/:id" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
            <Route path="/admin/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/admin/support/:id" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
            <Route path="/admin/videos" element={<ProtectedRoute><VideosRoleGate /></ProtectedRoute>} />
            <Route path="/admin/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
            <Route path="/admin/verify/:tier" element={<ProtectedRoute><TierVerificationPage /></ProtectedRoute>} />
            <Route path="/admin/invite" element={<ProtectedRoute><InviteUsers /></ProtectedRoute>} />
            <Route path="/admin/my-referrals" element={<ProtectedRoute><MyReferrals /></ProtectedRoute>} />
            <Route path="/admin/my-verified-lois" element={<ProtectedRoute><MyVerifiedLOIsPage /></ProtectedRoute>} />
            <Route path="/admin/my-created-lois" element={<ProtectedRoute><MyCreatedLOIs /></ProtectedRoute>} />
            <Route path="/admin/my-tasks" element={<ProtectedRoute><MyTodos /></ProtectedRoute>} />
            <Route path="/admin/verify-loi-internal" element={<ProtectedRoute><VerifyLOIPage /></ProtectedRoute>} />

            {/* Main Admin Routes (Multi-Tenant) - Restricted to main admin only */}
            <Route path="/admin/companies" element={<MainAdminRoute><MainAdminCompanies /></MainAdminRoute>} />
            <Route path="/admin/applications" element={<MainAdminRoute><MainAdminApplications /></MainAdminRoute>} />
            <Route path="/admin/membership-plans" element={<MainAdminRoute><MainAdminMembershipPlans /></MainAdminRoute>} />
            <Route path="/admin/coupons" element={<MainAdminRoute><AdminCoupons /></MainAdminRoute>} />
            <Route path="/admin/stripe-settings" element={<MainAdminRoute><MainAdminStripeSettings /></MainAdminRoute>} />
            <Route path="/admin/membership-settings" element={<MainAdminRoute><AdminMembershipSettings /></MainAdminRoute>} />
            <Route path="/admin/financial" element={<MainAdminRoute><AdminFinancialOverview /></MainAdminRoute>} />
            <Route path="/admin/financial/subscribers" element={<MainAdminRoute><AdminFinancialSubscribers /></MainAdminRoute>} />
            <Route path="/admin/financial/payments" element={<MainAdminRoute><AdminFinancialPayments /></MainAdminRoute>} />
            <Route path="/admin/financial/forecast" element={<MainAdminRoute><AdminFinancialForecast /></MainAdminRoute>} />
            <Route path="/admin/financial/receipts" element={<MainAdminRoute><AdminFinancialReceipts /></MainAdminRoute>} />
            <Route path="/admin/commodities" element={<MainAdminRoute><AdminCommodities /></MainAdminRoute>} />
            <Route path="/admin/directory" element={<MainAdminRoute><AdminDirectory /></MainAdminRoute>} />
            <Route path="/admin/cd" element={<MainAdminRoute><AdminDirectoryDump /></MainAdminRoute>} />
            <Route path="/admin/business-info" element={<ProtectedRoute><EditBusinessInfo /></ProtectedRoute>} />
            <Route path="/admin/network-badge" element={<ProtectedRoute><NetworkBadge /></ProtectedRoute>} />
            <Route path="/admin/directory-claims" element={<MainAdminRoute><AdminDirectoryClaims /></MainAdminRoute>} />
            <Route path="/admin/api-settings" element={<Navigate to="/admin/integration-settings?tab=api-keys" replace />} />
            <Route path="/admin/public-theme" element={<MainAdminRoute><AdminPublicTheme /></MainAdminRoute>} />
            <Route path="/admin/analytics" element={<MainAdminRoute><AdminAnalyticsSettings /></MainAdminRoute>} />
            <Route path="/admin/disclaimer" element={<MainAdminRoute><AdminDisclaimerSettings /></MainAdminRoute>} />
            <Route path="/admin/dashboard-control" element={<MainAdminRoute><AdminDashboardControl /></MainAdminRoute>} />
            <Route path="/admin/security-settings" element={<MainAdminRoute><AdminSecuritySettings /></MainAdminRoute>} />
            <Route path="/admin/leads" element={<MainAdminRoute><AdminLeads /></MainAdminRoute>} />
            <Route path="/admin/my-company-listing" element={<ProtectedRoute requireEmailVerification={false}><MyCompanyListing /></ProtectedRoute>} />
            <Route path="/admin/share-selling-process" element={<ProtectedRoute requireEmailVerification={false}><ShareSellingProcess /></ProtectedRoute>} />
            <Route path="/admin/trados-agent" element={<MainAdminRoute><AdminTradosAgentChat /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/overview" element={<MainAdminRoute><AdminTradosAgentOverview /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/tasks" element={<MainAdminRoute><AdminTradosAgentTasks /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/suggestions" element={<MainAdminRoute><AdminTradosAgentSuggestions /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/history" element={<MainAdminRoute><AdminTradosAgentHistory /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/memory" element={<MainAdminRoute><AdminTradosAgentMemory /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/files" element={<MainAdminRoute><AdminTradosAgentFiles /></MainAdminRoute>} />
            <Route path="/admin/trados-agent/settings" element={<MainAdminRoute><AdminTradosAgentSettings /></MainAdminRoute>} />
            <Route path="/admin/company-approval-steps" element={<MainAdminRoute><AdminCompanyApprovalSteps /></MainAdminRoute>} />
            <Route path="/admin/company-approval-questions" element={<MainAdminRoute><AdminCompanyApprovalQuestions /></MainAdminRoute>} />
            <Route path="/admin/company-verifications" element={<MainAdminRoute><AdminCompanyVerifications /></MainAdminRoute>} />
            <Route path="/admin/platform-analytics" element={<MainAdminRoute><PlatformAnalyticsDashboard /></MainAdminRoute>} />
            <Route path="/admin/referral-tree" element={<MainAdminRoute><React.Suspense fallback={<div className="p-8 text-center">Loading...</div>}><ReferralTree /></React.Suspense></MainAdminRoute>} />
            <Route path="/admin/global-users" element={<MainAdminRoute><AdminGlobalUsers /></MainAdminRoute>} />
            <Route path="/admin/access-requests" element={<ProtectedRoute adminOnly><AdminAccessRequests /></ProtectedRoute>} />
            <Route path="/admin/company-settings" element={<ProtectedRoute><CompanySettings /></ProtectedRoute>} />
            <Route path="/admin/company-questions" element={<ProtectedRoute adminOnly><CompanyQuestions /></ProtectedRoute>} />
            <Route path="/admin/billing" element={<ProtectedRoute adminOnly><Billing /></ProtectedRoute>} />
            <Route path="/subscription-required" element={<ProtectedRoute><SubscriptionRequired /></ProtectedRoute>} />
            <Route path="/company/verification" element={<ProtectedRoute><CompanyVerificationPortal /></ProtectedRoute>} />
            <Route path="/company/setup" element={<ProtectedRoute><CompanySetupWizard /></ProtectedRoute>} />
            <Route path="/admin/company/verification" element={<ProtectedRoute><CompanyVerificationPortal /></ProtectedRoute>} />
            <Route path="/admin/company/setup" element={<ProtectedRoute><CompanySetupWizard /></ProtectedRoute>} />
            
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminManageUsers /></ProtectedRoute>} />
            <Route path="/admin/users/:userId" element={<ProtectedRoute adminOnly><AdminUserDetails /></ProtectedRoute>} />
            <Route path="/admin/users/:userId/edit" element={<ProtectedRoute adminOnly><AdminUserEdit /></ProtectedRoute>} />
            <Route path="/admin/users/legacy" element={<ProtectedRoute adminOnly><AdminUserApprovals /></ProtectedRoute>} />
            <Route path="/admin/account-types" element={<ProtectedRoute adminOnly><AdminAccountTypes /></ProtectedRoute>} />
            <Route path="/admin/tier-approvals" element={<ProtectedRoute adminOnly><AdminTierApprovals /></ProtectedRoute>} />
            <Route path="/admin/tier-questions" element={<MainAdminRoute><AdminTierQuestions /></MainAdminRoute>} />
            <Route path="/admin/deals" element={<ProtectedRoute adminOnly><AdminDeals /></ProtectedRoute>} />
            <Route path="/admin/deal-categories" element={<ProtectedRoute adminOnly><AdminDealCategories /></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute adminOnly><AdminAnnouncements /></ProtectedRoute>} />
            <Route path="/admin/announcement-categories" element={<ProtectedRoute adminOnly><AdminAnnouncementCategories /></ProtectedRoute>} />
            <Route path="/admin/support" element={<ProtectedRoute adminOnly><AdminSupport /></ProtectedRoute>} />
            <Route path="/admin/support/:id" element={<ProtectedRoute adminOnly><AdminSupport /></ProtectedRoute>} />
            {/* /admin/videos handled higher up by VideosRoleGate (admins ->
                AdminVideos manager, everyone else -> VideosPage viewer). This
                duplicate route was NEVER reached (first-match wins) and was
                why super admins couldn't find the video-add UI. */}
            <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/settings/sidebar" element={<ProtectedRoute adminOnly><AdminSidebarSettings /></ProtectedRoute>} />
            <Route path="/admin/theme" element={<ProtectedRoute adminOnly><AdminThemeSettings /></ProtectedRoute>} />
            <Route path="/admin/theme-presets" element={<ProtectedRoute adminOnly><AdminThemePresets /></ProtectedRoute>} />
            <Route path="/admin/email-logs" element={<ProtectedRoute adminOnly><AdminEmailLogs /></ProtectedRoute>} />
            <Route path="/admin/logs" element={<ProtectedRoute adminOnly><AdminLogFiles /></ProtectedRoute>} />
            <Route path="/admin/upload-settings" element={<ProtectedRoute adminOnly><AdminUploadSettings /></ProtectedRoute>} />
            <Route path="/admin/upload-settings/folder/*" element={<ProtectedRoute adminOnly><AdminUploadFolder /></ProtectedRoute>} />
            <Route path="/admin/documents" element={<ProtectedRoute adminOnly><AdminDocuments /></ProtectedRoute>} />
            <Route path="/admin/process-steps" element={<ProtectedRoute adminOnly><AdminProcessSteps /></ProtectedRoute>} />
            <Route path="/admin/process-templates" element={<ProtectedRoute adminOnly><AdminProcessTemplates /></ProtectedRoute>} />
            <Route path="/admin/question-templates" element={<MainAdminRoute><AdminQuestionTemplates /></MainAdminRoute>} />
            <Route path="/admin/process-templates/:templateId" element={<ProtectedRoute adminOnly><AdminProcessTemplateEdit /></ProtectedRoute>} />
            <Route path="/admin/quote-requests" element={<ProtectedRoute adminOnly><AdminQuoteRequests /></ProtectedRoute>} />
            <Route path="/admin/quote-requests/:id" element={<ProtectedRoute adminOnly><AdminQuoteRequestDetail /></ProtectedRoute>} />
            <Route path="/admin/questions/broker" element={<ProtectedRoute adminOnly><AdminBrokerQuestions /></ProtectedRoute>} />
            <Route path="/admin/questions/buyer" element={<ProtectedRoute adminOnly><AdminBuyerQuestions /></ProtectedRoute>} />
            <Route path="/admin/questions/loi" element={<ProtectedRoute adminOnly><AdminLOIQuestions /></ProtectedRoute>} />
            <Route path="/admin/questions/user" element={<ProtectedRoute adminOnly><AdminUserQuestions /></ProtectedRoute>} />
            <Route path="/admin/requests" element={<ProtectedRoute adminOnly><AdminRequests /></ProtectedRoute>} />
            <Route path="/admin/requests/:id" element={<ProtectedRoute adminOnly><AdminRequestDetail /></ProtectedRoute>} />
            <Route path="/admin/integration-settings" element={<MainAdminRoute><AdminIntegrationSettings /></MainAdminRoute>} />
            <Route path="/admin/external-services" element={<MainAdminRoute><AdminExternalServices /></MainAdminRoute>} />
            <Route path="/admin/intel-settings" element={<MainAdminRoute><AdminIntelSettings /></MainAdminRoute>} />

            <Route path="/admin/loi-verification-items" element={<Navigate to="/admin/external-services?tab=check-items" replace />} />
            <Route path="/admin/email-templates" element={<MainAdminRoute><AdminEmailTemplates /></MainAdminRoute>} />
            <Route path="/admin/newsletters" element={<MainAdminRoute><AdminNewsletters /></MainAdminRoute>} />
            <Route path="/admin/posting" element={<MainAdminRoute><AdminPosting /></MainAdminRoute>} />
            <Route path="/admin/created-lois" element={<MainAdminRoute><AdminCreatedLOIs /></MainAdminRoute>} />

            {/* Catch-all: redirect unmatched routes to dashboard */}
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
          <GlobalChatWidget />
          <Toaster position="top-right" />
        </div>
      );
}

export default App;
